import { Mail, RotateCcw, Save, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { useParent } from '../ParentContext';

type Kind = 'received' | 'approved' | 'declined' | 'warning' | 'ended' | 'unclaimed';
interface AmbassadorEmail {
  kind: Kind;
  on: boolean;
  subject: string;
  body: string;
  edited: boolean;
  title: string;
  when: string;
  group: string;
  eyebrow: string;
  heading: string;
  tokens: string[];
  needs: string[];
  builtIn: { subject: string; body: string };
}

/** What the preview fills in, so the wording reads like a real email. */
const SAMPLE: Record<string, string> = {
  name: 'Jordan',
  code: 'JORDAN20',
  link: 'classguardian.app/?partner=JORDAN20',
  months: '12',
  end_date: 'November 27, 2026',
  access_ends: 'December 27, 2026',
  plan: 'Ambassador Plan',
  offer: 'Families who use it get 20% off their first 3 months. You earn 25% of what they pay for their first 12 months.',
  sign_in: 'Your sign-ups, earnings and payouts are on your Brand Ambassador page: sign in at classguardian.app/login and open the Ambassador tab.',
  unpaid_line: 'You also have $48.20 waiting. Set up payouts on your Brand Ambassador page so we can send it.',
  amount: '$48.20',
  page_link: 'classguardian.app/ambassador',
  apply_link: 'classguardian.app/ambassadors',
  terms_link: 'classguardian.app/ambassador-terms',
  contact_line: 'Questions? Write to ambassadors@classguardian.app.',
};
const fill = (t: string) => t.replace(/\{\{(\w+)\}\}/g, (all, k: string) => SAMPLE[k] ?? all).replace(/\n{3,}/g, '\n\n');

/**
 * Every email a Brand Ambassador gets (#895), with wording the admin can change: subject and body with fields, on or
 * off, a test sent to the admin, and the built-in wording back.
 */
export function AmbassadorEmails({ onClose }: { onClose: () => void }) {
  const { toast } = useParent();
  const [emails, setEmails] = useState<AmbassadorEmail[] | null>(null);
  const [kind, setKind] = useState<Kind>('warning');
  const [draft, setDraft] = useState<{ on: boolean; subject: string; body: string } | null>(null);
  const [busy, setBusy] = useState('');
  const load = (r: { emails: AmbassadorEmail[] }) => setEmails(r.emails);
  useEffect(() => {
    api
      .get<{ emails: AmbassadorEmail[] }>('/api/admin/ambassador-emails')
      .then(load)
      .catch((e) => toast({ text: (e as Error).message, tone: 'alert' }));
  }, [toast]);
  const e = emails?.find((x) => x.kind === kind);
  useEffect(() => {
    if (e) setDraft({ on: e.on, subject: e.subject, body: e.body });
  }, [e]);
  if (!emails || !e || !draft) return null;
  const missing = e.needs.filter((k) => !draft.body.includes(`{{${k}}}`));
  const changed = draft.on !== e.on || draft.subject !== e.subject || draft.body !== e.body;

  async function act(what: 'save' | 'restore' | 'test') {
    setBusy(what);
    try {
      if (what === 'save') load(await api.put<{ emails: AmbassadorEmail[] }>(`/api/admin/ambassador-emails/${kind}`, draft));
      if (what === 'restore') load(await api.post<{ emails: AmbassadorEmail[] }>(`/api/admin/ambassador-emails/${kind}/restore`));
      if (what === 'test') {
        const r = await api.post<{ to: string }>(`/api/admin/ambassador-emails/${kind}/test`);
        toast({ text: `Sent the saved version to ${r.to}.`, tone: 'good' });
      } else toast({ text: what === 'save' ? 'Saved.' : 'The built-in wording is back.', tone: 'good' });
    } catch (err) {
      toast({ text: (err as Error).message, tone: 'alert' });
    } finally {
      setBusy('');
    }
  }

  return (
    <section className="card stack bn" aria-label="Ambassador emails">
      <div className="row-between wrap">
        <h3>Ambassador emails</h3>
        <button type="button" className="icon-btn" aria-label="Close ambassador emails" onClick={onClose}>
          <X size={18} />
        </button>
      </div>
      <p className="muted small">Every email a Brand Ambassador gets, sent from your server’s email. Change the wording, turn one off, or send yourself a test.</p>
      <div className="bn-layout">
        <nav className="bn-list" aria-label="Ambassador emails">
          {emails.map((x) => (
            <button key={x.kind} type="button" className={x.kind === kind ? 'on' : ''} onClick={() => setKind(x.kind)}>
              <span className="bn-name">{x.title}</span>
              <span className={`bn-pill ${x.on ? 'on' : ''}`}>{x.on ? (x.edited ? 'Edited' : 'On') : 'Off'}</span>
              <span className="muted small bn-when">{x.group}</span>
            </button>
          ))}
        </nav>

        <div className="stack bn-main">
          <div className="row-between wrap">
            <h4>{e.title}</h4>
            <label className="check">
              <input type="checkbox" checked={draft.on} onChange={(ev) => setDraft({ ...draft, on: ev.target.checked })} /> Send this email
            </label>
          </div>
          <p className="muted small">{e.when}</p>
          <div className="bn-editor">
            <div className="stack">
              <label>
                Subject
                <input value={draft.subject} maxLength={200} onChange={(ev) => setDraft({ ...draft, subject: ev.target.value })} />
              </label>
              <label>
                Body
                <textarea rows={12} value={draft.body} maxLength={5000} onChange={(ev) => setDraft({ ...draft, body: ev.target.value })} />
              </label>
              <div className="bn-tokens" aria-label="Fields you can use">
                {e.tokens.map((k) => (
                  <button key={k} type="button" onClick={() => setDraft({ ...draft, body: `${draft.body}{{${k}}}` })}>
                    {`{{${k}}}`}
                  </button>
                ))}
              </div>
              {missing.length > 0 && (
                <p className="bn-missing small" role="alert">
                  The body needs {missing.map((k) => `{{${k}}}`).join(', ')} before it can be saved.
                </p>
              )}
            </div>
            <div className="bn-mail" aria-label="Preview">
              <span className="muted small">
                Subject: <strong>{fill(draft.subject)}</strong>
              </span>
              <div className="bn-mail-card">
                <div className="bn-mail-head">
                  <span>{e.eyebrow}</span>
                  <strong>{fill(e.heading)}</strong>
                </div>
                <div className="bn-mail-body">{fill(draft.body).trim()}</div>
              </div>
            </div>
          </div>
          <div className="row-gap bn-actions">
            <button type="button" className="btn primary" onClick={() => void act('save')} disabled={!!busy || !changed || missing.length > 0}>
              <Save size={16} /> {busy === 'save' ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="btn" onClick={() => void act('test')} disabled={!!busy}>
              <Mail size={16} /> {busy === 'test' ? 'Sending…' : 'Send me a test'}
            </button>
            <button type="button" className="btn" onClick={() => void act('restore')} disabled={!!busy || !e.edited}>
              <RotateCcw size={16} /> Restore built-in
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
